/**
 * トレーニング経過の詳細(D-197)。1人 × 1カテゴリ。
 *  - 概要(稼働開始日・累計・次の段階)。稼働開始日は管理者が指定できる
 *  - 面談予定日(管理者が入れる)と、その前の30日間の成績(2026-10-15 発注者指示)
 *  - 段階ごとの成績(区間の取得・時間・1時間あたり・前の段階との差・控えの保存)
 *  - 業務(会社)別の内訳
 *  - 面談・研修の記録と、面談の前後15hの1時間あたり取得の差
 */
import { useState } from 'react';
import DetailPanel, { DetailList, DetailSection } from '@core/components/ui/DetailPanel';
import Modal from '@core/components/Modal';
import DateField from '@core/components/DateField';
import StatusBadge from '@core/components/ui/StatusBadge';
import { useToast } from '@core/components/Toast';
import { todayStr, toDateLabel } from '@core/lib/date';
import { BUSINESS_LABELS, type Company, type DateStr } from '@core/lib/types';
import { formatCount, formatDecimal, formatDuration, formatPercent, formatPointDiff, NO_DATA } from '@core/utils/formatters';
import ToneValue from '@core/components/ui/ToneValue';
import { companyBreakdown, countsForEffect, EFFECT_HOURS, monthlyStats, type MonthAverage, hoursBefore, MILESTONES, PLAN_WINDOW_DAYS, planTargets, planWindowStats, rateOf, rateTarget, RECORD_KIND_LABELS, recordEffect, savedMilestoneKey, type SavedMilestone, type Track, type TrainingRecord, type TrainingTrackDoc } from './training';
import { setInterviewPlanDate, setTrackStartDate } from './trainingRepo';
import { fmtRate, fmtSec, TargetRate, TargetValue, partialNote, RateDiff } from './trainingFormat';

const START_SOURCE_LABEL = { sheet: '採用データ', manual: '指定', auto: '最初の稼働日' } as const;
const fmtH = (v: number | null | undefined) => formatDecimal(v, { digits: 2, unit: 'h' });

/**
 * 目標との差(実績 − 目標)。目標を満たせば緑・満たさなければ赤。
 * higher = 目標以上が良い(稼働・取得・取得率・通話件数)。それ以外(通話時間・後処理時間)は短いほど良い(2026-10-15 発注者回答)
 */
function TargetDiff({ diff, text, higher }: { diff: number | null; text: string; higher: boolean }) {
  if (diff === null) return <>{NO_DATA}</>;
  // 表示する桁で判定する(−0.004 を「未達」にしない)
  const d = Number(diff.toFixed(2));
  const ok = higher ? d >= 0 : d <= 0;
  return (
    <ToneValue tone={ok ? 'good' : 'negative'} srText={ok ? '(目標達成)' : '(目標未達)'}>
      {text}
    </ToneValue>
  );
}
const signedText = (v: number, digits: number, unit: string) => {
  const r = Number(v.toFixed(digits));
  return `${r > 0 ? '+' : ''}${formatDecimal(r === 0 ? 0 : r, { digits, unit })}`;
};

export default function TrainingDetailPanel({
  track: t,
  trackDoc,
  records,
  saved,
  monthAverages,
  companies,
  userId,
  onAdd,
  onEdit,
  onChanged,
  onClose,
}: {
  track: Track;
  trackDoc: TrainingTrackDoc | undefined;
  records: TrainingRecord[];
  saved: SavedMilestone[];
  /** その月の全員(同じカテゴリ)の平均(2026-10-20 発注者指示) */
  monthAverages: Map<string, MonthAverage>;
  companies: Company[];
  userId: string;
  onAdd: () => void;
  onEdit: (r: TrainingRecord) => void;
  onChanged: () => void;
  onClose: () => void;
}) {
  const toast = useToast();
  const [editingStart, setEditingStart] = useState(false);
  const [startValue, setStartValue] = useState<DateStr>(t.startDate ?? todayStr());
  const [busy, setBusy] = useState(false);
  const planDate = trackDoc?.interview_plan_date ?? null;
  const [editingPlan, setEditingPlan] = useState(false);
  const [planValue, setPlanValue] = useState<DateStr>(planDate ?? todayStr());
  const plan = planDate ? planWindowStats(t.days, planDate) : null;
  const target = planTargets(t.business);
  const months = monthlyStats(t.days);
  const totalRate = rateOf(t.totalAppointments, t.totalHours);
  /** 値の右に「目標 ○ ／ 差」を並べる(目標が無い・値が無いときは値だけ) */
  const withGoal = (node: React.ReactNode, value: number | null, goal: number | null, higher: boolean, digits: number, unit: string, goalText?: string) =>
    goal === null || value === null ? (
      node
    ) : (
      <span className="training-goal">
        {node}
        <span className="training-goal-diff">
          <span className="ui-hint">目標 {goalText ?? formatDecimal(goal, { digits: 0, unit })}</span>
          <TargetDiff diff={value - goal} text={signedText(value - goal, digits, unit)} higher={higher} />
        </span>
      </span>
    );
  // 面談予定日の前の30日間の6項目と目標・差(2026-10-15 発注者指示)
  const planRows = plan
    ? [
        { label: '稼働時間', value: fmtH(plan.hours), sub: `${plan.days}日`, goal: target ? `${target.hours}h以上` : NO_DATA, diff: target ? plan.hours - target.hours : null, diffText: (d: number) => signedText(d, 2, 'h'), higher: true },
        { label: '取得成績/h', value: `${fmtRate(plan.rate)}件/h`, sub: `${formatCount(plan.appointments)}件`, goal: target ? `${fmtRate(target.rate)}件/h` : NO_DATA, diff: target && plan.rate !== null ? plan.rate - target.rate : null, diffText: (d: number) => signedText(d, 2, ''), higher: true },
        { label: '取得率', value: formatPercent(plan.hitRate), sub: `通話 ${formatCount(plan.calls)}件`, goal: target ? formatPercent(target.hitRate) : NO_DATA, diff: target && plan.hitRate !== null ? plan.hitRate - target.hitRate : null, diffText: (d: number) => formatPointDiff(d), higher: true },
        { label: '通話件数/h', value: formatDecimal(plan.callsPerHour, { digits: 1, unit: '件/h' }), sub: '', goal: target ? `${target.callsPerHour}件/h` : NO_DATA, diff: target && plan.callsPerHour !== null ? plan.callsPerHour - target.callsPerHour : null, diffText: (d: number) => signedText(d, 1, ''), higher: true },
        { label: '通話時間', value: `平均 ${fmtSec(plan.talkAvgSec)}`, sub: `計 ${formatDuration(plan.talkSec)}`, goal: target ? `平均 ${target.talkAvgSec}秒` : NO_DATA, diff: target && plan.talkAvgSec !== null ? plan.talkAvgSec - target.talkAvgSec : null, diffText: (d: number) => signedText(d, 1, '秒'), higher: false },
        { label: '後処理時間', value: `平均 ${fmtSec(plan.afterAvgSec)}`, sub: `計 ${formatDuration(plan.afterSec)}`, goal: target ? `平均 ${target.afterAvgSec}秒` : NO_DATA, diff: target && plan.afterAvgSec !== null ? plan.afterAvgSec - target.afterAvgSec : null, diffText: (d: number) => signedText(d, 1, '秒'), higher: false },
      ]
    : [];
  const savedById = new Map(saved.map((s) => [s.id, s]));
  const companyName = (id: string) => (id === '' ? '採用データ(会社は不明)' : (companies.find((c) => c.id === id)?.name ?? '割振なし'));
  const breakdown = companyBreakdown(t.days);

  async function saveStart(value: DateStr | null) {
    setBusy(true);
    try {
      await setTrackStartDate(t.opId, t.business, value, trackDoc?.sheet_start_date ?? null, userId);
      toast(value ? `稼働開始日を ${toDateLabel(value)} にしました` : '稼働開始日の指定を外しました');
      setEditingStart(false);
      onChanged();
    } catch (e) {
      toast(e instanceof Error ? `保存できませんでした: ${e.message}` : '保存できませんでした');
    } finally {
      setBusy(false);
    }
  }

  async function savePlan(value: DateStr | null) {
    setBusy(true);
    try {
      await setInterviewPlanDate(t.opId, t.business, value, userId);
      toast(value ? `面談予定日を ${toDateLabel(value)} にしました` : '面談予定日を消しました');
      setEditingPlan(false);
      onChanged();
    } catch (e) {
      toast(e instanceof Error ? `保存できませんでした: ${e.message}` : '保存できませんでした');
    } finally {
      setBusy(false);
    }
  }

  return (
    <DetailPanel
      title={t.name}
      subtitle={`${t.opCode} ・ ${BUSINESS_LABELS[t.business]}${t.isNew ? ' ・ 新人' : ''}`}
      onClose={onClose}
      footer={
        <button type="button" className="ui-btn ui-btn-primary" onClick={onAdd}>
          ＋ 面談・研修・指導を記録
        </button>
      }
    >
      <DetailSection
        title="概要"
        actions={
          <>
            <button
              type="button"
              className="ui-btn ui-btn-secondary ui-btn-sm"
              onClick={() => {
                setPlanValue(planDate ?? todayStr());
                setEditingPlan(true);
              }}
            >
              {planDate ? '面談予定日を変更' : '面談予定日を入力'}
            </button>
            <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={() => setEditingStart(true)}>
              稼働開始日を変更
            </button>
          </>
        }
      >
        <DetailList
          items={[
            { label: '稼働開始日', value: t.startDate ? `${toDateLabel(t.startDate)}(${START_SOURCE_LABEL[t.startSource ?? 'manual']})` : '不明' },
            { label: '最終稼働日', value: t.lastWorkDate ? toDateLabel(t.lastWorkDate) : '稼働なし' },
            { label: '数え始め', value: t.isNew ? toDateLabel(t.countFrom) : `${toDateLabel(t.countFrom)}(5/1 より前から在籍・不明のため)` },
            { label: '累計稼働', value: fmtH(t.totalHours), numeric: true },
            { label: '累計の取得', value: formatCount(t.totalAppointments), numeric: true },
            // 目標対比を値の右に(2026-10-20 発注者指示。目標は面談予定日の目標と同じ・給湯器だけ)
            { label: '1時間あたり', value: withGoal(<TargetValue value={totalRate} target={target?.rate ?? null} higher text={fmtRate(totalRate)} unit="件/h" />, totalRate, target?.rate ?? null, true, 2, '件', fmtRate(target?.rate)), numeric: true },
            // 通話の数字(数え始めから。採用データの日は入らない。2026-10-20 発注者指示)
            { label: '通話時間Av', value: withGoal(<TargetValue value={t.calls.talkAvgSec} target={target?.talkAvgSec ?? null} higher={false} text={fmtSec(t.calls.talkAvgSec)} unit="秒" />, t.calls.talkAvgSec, target?.talkAvgSec ?? null, false, 1, '秒'), numeric: true },
            { label: '後処理時間Av', value: withGoal(<TargetValue value={t.calls.afterAvgSec} target={target?.afterAvgSec ?? null} higher={false} text={fmtSec(t.calls.afterAvgSec)} unit="秒" />, t.calls.afterAvgSec, target?.afterAvgSec ?? null, false, 1, '秒'), numeric: true },
            { label: '1時間あたりの通話回数', value: withGoal(<TargetValue value={t.calls.callsPerHour} target={target?.callsPerHour ?? null} higher text={formatDecimal(t.calls.callsPerHour, { digits: 1, unit: '回' })} unit="回" />, t.calls.callsPerHour, target?.callsPerHour ?? null, true, 1, '回'), numeric: true },
            {
              label: '次の段階',
              value: t.next ? `${t.next.milestone}h まで あと ${fmtH(t.next.remaining)}(この区間 ${t.next.segAppointments}件 / ${fmtH(t.next.segHours)})` : '—',
            },
          ]}
        />
        <h4 className="training-plan-title">
          面談予定日 {planDate ? toDateLabel(planDate) : <span className="ui-hint">未定</span>}
        </h4>
        {plan && (
          <>
            <p className="ui-hint">
              予定日の前の{PLAN_WINDOW_DAYS}日間({toDateLabel(plan.from)}〜{toDateLabel(plan.to)})
              {plan.hasSheetDays ? '(採用データの日は通話の記録が無いため、通話の数字に入りません)' : ''}
            </p>
            {plan.days === 0 ? (
              <p className="ui-hint">この期間の稼働はありません</p>
            ) : (
              <div className="report-scroll is-auto">
                <table className="report-table no-stack">
                  <caption className="sr-only">面談予定日の前の{PLAN_WINDOW_DAYS}日間の成績と目標</caption>
                  <thead>
                    <tr>
                      <th scope="col">項目</th>
                      <th scope="col" className="is-num">{PLAN_WINDOW_DAYS}日間</th>
                      <th scope="col" className="is-num">目標</th>
                      <th scope="col" className="is-num">差</th>
                    </tr>
                  </thead>
                  <tbody>
                    {planRows.map((r) => (
                      <tr key={r.label}>
                        <th scope="row">{r.label}</th>
                        <td className="is-num">
                          {r.value}
                          {r.sub && <span className="training-plan-sub">{r.sub}</span>}
                        </td>
                        <td className="is-num">{r.goal}</td>
                        <td className="is-num">
                          <TargetDiff diff={r.diff} text={r.diff === null ? '' : r.diffText(r.diff)} higher={r.higher} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </DetailSection>

      <DetailSection title="段階ごとの成績">
        {t.milestones.length === 0 ? (
          <p className="ui-hint">まだ 15h に届いていません</p>
        ) : (
          <div className="report-scroll is-auto">
            <table className="report-table no-stack">
              <caption className="sr-only">段階ごとの成績</caption>
              <thead>
                {/* 経過(前の段階からの区間)と累計(稼働開始からその段階まで)を並べる(2026-10-14 発注者指示) */}
                <tr>
                  <th scope="col" rowSpan={2}>段階</th>
                  <th scope="col" rowSpan={2}>到達日</th>
                  <th scope="colgroup" colSpan={4} className="is-center">経過</th>
                  <th scope="colgroup" colSpan={4} className="is-center col-group">累計</th>
                  <th scope="col" rowSpan={2}>控え</th>
                </tr>
                <tr>
                  <th scope="col" className="is-num">取得</th>
                  <th scope="col" className="is-num">時間</th>
                  <th scope="col" className="is-num">件/h</th>
                  <th scope="col" className="is-num">前との差</th>
                  <th scope="col" className="is-num col-group">取得</th>
                  <th scope="col" className="is-num">時間</th>
                  <th scope="col" className="is-num">件/h</th>
                  <th scope="col" className="is-num">前との差</th>
                </tr>
              </thead>
              <tbody>
                {t.milestones.map((m) => {
                  const s = savedById.get(savedMilestoneKey(t.opId, t.business, m.milestone));
                  const same = s && s.reached_date === m.reachedDate && s.seg_appointments === m.segAppointments;
                  return (
                    <tr key={m.milestone}>
                      <th scope="row">{m.milestone}h</th>
                      <td>{m.reachedDate.slice(5).replace('-', '/')}</td>
                      <td className="is-num">{formatCount(m.segAppointments)}</td>
                      <td className="is-num">{fmtH(m.segHours)}</td>
                      <td className="is-num">
                        <TargetRate value={m.segRate} target={rateTarget(t.business, m.milestone)} />
                      </td>
                      <td className="is-num">
                        <RateDiff value={m.diff} />
                      </td>
                      <td className="is-num col-group">{formatCount(m.cumAppointments)}</td>
                      <td className="is-num">{fmtH(m.cumHours)}</td>
                      <td className="is-num">
                        <TargetRate value={m.cumRate} target={rateTarget(t.business, m.milestone)} />
                      </td>
                      <td className="is-num">
                        <RateDiff value={m.cumDiff} />
                      </td>
                      <td>{!(MILESTONES as readonly number[]).includes(m.milestone) ? <span className="ui-hint">—</span> : same ? <StatusBadge kind="confirmed" label="済" title="到達の成績を保存済み" /> : s ? <StatusBadge kind="warning" label="変更" title="保存したあとに数字が変わりました(取込のやり直しなど)" /> : <StatusBadge kind="unconfirmed" label="未" title="まだ保存していません" />}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </DetailSection>

      {/* 月別の成績と、その月の全員(同じカテゴリ)の平均(2026-10-20 発注者指示)。平均より良い月は緑の太字 */}
      <DetailSection title="月別の成績">
        {months.length === 0 ? (
          <p className="ui-hint">稼働の記録がありません</p>
        ) : (
          <>
            <p className="ui-hint">下段は、その月に稼働した{BUSINESS_LABELS[t.business]}の全員の平均(1人ずつの月の値の平均)。通話の数字は採用データの日を含みません</p>
            <div className="report-scroll is-auto">
              <table className="report-table no-stack training-months">
                <caption className="sr-only">月別の成績と全員の平均</caption>
                <thead>
                  <tr>
                    <th scope="col">月</th>
                    <th scope="col" className="is-num">稼働</th>
                    <th scope="col" className="is-num">取得</th>
                    <th scope="col" className="is-num">件/h</th>
                    <th scope="col" className="is-num">通話時間Av</th>
                    <th scope="col" className="is-num">後処理時間Av</th>
                    <th scope="col" className="is-num">通話回数/h</th>
                  </tr>
                </thead>
                <tbody>
                  {months.map((m) => {
                    const a = monthAverages.get(m.month);
                    const cell = (mine: number | null, avg: number | null | undefined, fmt: (v: number | null) => string, higher: boolean) => {
                      // 表示する桁(小数2桁)で比べる(30.0 と 29.999… を「良い」にしない)
                      const r2 = (v: number) => Math.round(v * 100) / 100;
                      const better = mine !== null && avg !== null && avg !== undefined && (higher ? r2(mine) > r2(avg) : r2(mine) < r2(avg));
                      return (
                        <td className="is-num">
                          <ToneValue tone={better ? 'good' : ''}>{fmt(mine)}</ToneValue>
                          <span className="training-month-avg">{avg === undefined ? NO_DATA : fmt(avg)}</span>
                        </td>
                      );
                    };
                    return (
                      <tr key={m.month}>
                        <th scope="row">
                          {Number(m.month.slice(5, 7))}月<span className="training-month-avg">平均({a?.people ?? 0}名)</span>
                        </th>
                        {cell(m.hours, a?.hours, (v) => formatDecimal(v, { digits: 1, unit: 'h' }), true)}
                        {cell(m.appointments, a?.appointments, (v) => formatDecimal(v, { digits: v !== null && Number.isInteger(v) ? 0 : 1 }), true)}
                        {cell(m.rate, a?.rate, fmtRate, true)}
                        {cell(m.calls.talkAvgSec, a?.talkAvgSec, fmtSec, false)}
                        {cell(m.calls.afterAvgSec, a?.afterAvgSec, fmtSec, false)}
                        {cell(m.calls.callsPerHour, a?.callsPerHour, (v) => formatDecimal(v, { digits: 1 }), true)}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </DetailSection>

      <DetailSection title="業務(会社)別">
        {breakdown.length === 0 ? (
          <p className="ui-hint">稼働の記録がありません</p>
        ) : (
          <div className="report-scroll is-auto">
            <table className="report-table no-stack">
              <caption className="sr-only">業務(会社)別</caption>
              <thead>
                <tr>
                  <th scope="col">会社</th>
                  <th scope="col" className="is-num">時間</th>
                  <th scope="col" className="is-num">取得</th>
                  <th scope="col" className="is-num">件/h</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.map((b) => (
                  <tr key={b.companyId || 'sheet'}>
                    <td>{companyName(b.companyId)}</td>
                    <td className="is-num">{fmtH(b.hours)}</td>
                    <td className="is-num">{formatCount(b.appointments)}</td>
                    <td className="is-num">{fmtRate(rateOf(b.appointments, b.hours))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DetailSection>

      <DetailSection title={`面談・研修・指導の記録(${records.length}件)`}>
        {records.length === 0 ? (
          <p className="ui-hint">記録はまだありません</p>
        ) : (
          records.map((r) => {
            const eff = recordEffect(t.days, r.date);
            return (
              <article key={r.id} className="training-record">
                <div className="training-record-head">
                  <b>{toDateLabel(r.date)}</b>
                  <span className="ui-hint training-partial">({fmtH(hoursBefore(t.days, r.date))}時点)</span>
                  <span>{RECORD_KIND_LABELS[r.kind]}</span>
                  {r.milestone && <span className="ui-hint">{r.milestone}h の面談</span>}
                  <span className="ui-hint">担当 {r.trainer || NO_DATA}</span>
                  <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm training-record-edit" onClick={() => onEdit(r)}>
                    編集
                  </button>
                </div>
                {r.issues && r.issues.length > 0 && (
                  <p className="training-record-tags">
                    <span className="ui-hint">課題</span>
                    {r.issues.map((x) => (
                      <span key={x} className="training-record-tag">
                        {x}
                      </span>
                    ))}
                  </p>
                )}
                {r.actions && r.actions.length > 0 && (
                  <ul className="training-record-actions">
                    {r.actions.map((a, i) => (
                      <li key={i}>
                        <b>{a.tag}</b>
                        {a.memo && <span> {a.memo}</span>}
                      </li>
                    ))}
                  </ul>
                )}
                {r.report ? <p className="training-record-report">{r.report}</p> : !r.issues?.length && !r.actions?.length && <p className="ui-hint">備考は未入力です</p>}
                {/* 指導は面談前後15hの効果に入れない(2026-10-20 発注者回答) */}
                {countsForEffect(r.kind) && (
                  <DetailList
                    items={[
                      {
                        label: `前の${EFFECT_HOURS}h`,
                        value: eff.before ? `${fmtRate(eff.before.rate)}件/h(${eff.before.appointments}件 / ${fmtH(eff.before.hours)}・${toDateLabel(eff.before.from)}〜${toDateLabel(eff.before.to)})` : NO_DATA,
                      },
                      {
                        label: `後の${EFFECT_HOURS}h`,
                        value: eff.after
                          ? `${fmtRate(eff.after.rate)}件/h(${eff.after.appointments}件 / ${fmtH(eff.after.hours)}・${toDateLabel(eff.after.from)}〜${toDateLabel(eff.after.to)})${eff.afterComplete ? '' : ` ${partialNote(eff)}`}`
                          : '面談のあとの稼働がまだありません',
                      },
                      {
                        label: '差',
                        value:
                          eff.diff === null ? (
                            NO_DATA
                          ) : (
                            <>
                              <RateDiff value={eff.diff} /> {partialNote(eff)}
                            </>
                          ),
                        numeric: eff.diff !== null,
                      },
                    ]}
                  />
                )}
              </article>
            );
          })
        )}
      </DetailSection>

      {editingPlan && (
        <Modal onClose={() => setEditingPlan(false)} width="min(460px, 94vw)">
          <h3 className="training-modal-title">面談予定日</h3>
          <p className="ui-hint">予定日の前の{PLAN_WINDOW_DAYS}日間の成績を概要に出します。</p>
          <div className="training-actions">
            <DateField variant="ui" value={planValue} onChange={setPlanValue} />
          </div>
          <div className="training-actions">
            <button type="button" className="ui-btn ui-btn-primary" onClick={() => void savePlan(planValue)} disabled={busy || !planValue}>
              保存する
            </button>
            <button type="button" className="ui-btn ui-btn-secondary" onClick={() => void savePlan(null)} disabled={busy || !planDate}>
              予定日を消す
            </button>
            <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setEditingPlan(false)} disabled={busy}>
              やめる
            </button>
          </div>
        </Modal>
      )}

      {editingStart && (
        <Modal onClose={() => setEditingStart(false)} width="min(460px, 94vw)">
          <h3 className="training-modal-title">稼働開始日を変更</h3>
          <p className="ui-hint">5/1 以降の日付にすると、その日から 15h・30h… を数えます(新人)。指定を外すと採用データの稼働日に戻ります。</p>
          <div className="training-actions">
            <DateField variant="ui" value={startValue} onChange={setStartValue} />
          </div>
          <div className="training-actions">
            <button type="button" className="ui-btn ui-btn-primary" onClick={() => void saveStart(startValue)} disabled={busy}>
              保存する
            </button>
            <button type="button" className="ui-btn ui-btn-secondary" onClick={() => void saveStart(null)} disabled={busy || trackDoc?.start_source !== 'manual'}>
              指定を外す
            </button>
            <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setEditingStart(false)} disabled={busy}>
              やめる
            </button>
          </div>
        </Modal>
      )}
    </DetailPanel>
  );
}
